import { type UseMutationResult, useMutation } from "react-query";
import { globalState } from "../../../state";
import { BADGE_SALT } from "../../config/env";

export interface ICreateAccountRequest {
  username: string;
  password: string;
  token: string;
  privacyPolicyAccepted: boolean;
  campusName?: string;
  emailDomain?: string;
}

export const useVerifyEmail = (): UseMutationResult<
  { success: boolean; foreignEdu: boolean },
  unknown,
  { email: string }
> =>
  useMutation(({ email }: { email: string }) =>
    globalState.client.verifyEmail(email),
  );

export const useToken = (): UseMutationResult<
  {
    success: boolean;
    campusIdentified: boolean;
    emailDomain?: string;
  },
  unknown,
  { token: string }
> =>
  useMutation(({ token }: { token: string }) =>
    globalState.client.verifyToken(token),
  );

export const useCheckPasswordStrength = (): UseMutationResult<
  void,
  unknown,
  { password: string }
> =>
  useMutation(({ password }: { password: string }) =>
    globalState.client.checkPasswordStrength(password),
  );

export const useCreateAccount = (): UseMutationResult<
  void,
  unknown,
  ICreateAccountRequest
> =>
  useMutation((request: ICreateAccountRequest) =>
    globalState.client.createAccount(
      request.username,
      request.password,
      request.token,
      request.privacyPolicyAccepted,
      request.campusName,
      request.emailDomain,
    ),
  );

export const useBackupCode = (): UseMutationResult<
  void,
  unknown,
  { code: string }
> =>
  useMutation(({ code }: { code: string }) =>
    globalState.client.useBackupCode(code, BADGE_SALT),
  );

export const useBurnBackupCode = (): UseMutationResult<
  void,
  unknown,
  {
    code: string;
    password: string;
  }
> =>
  useMutation(({ code, password }: { code: string; password: string }) =>
    globalState.client.burnBackupCode(code, password, BADGE_SALT),
  );

export const useAccountRecoveryToken = (): UseMutationResult<
  {
    success: boolean;
    validation: {
      questions: string[];
    };
  },
  unknown,
  { token: string }
> =>
  useMutation(({ token }: { token: string }) =>
    globalState.client.verifyAccountRecoveryToken(token),
  );

export const useSignupCredentialsToken = (): UseMutationResult<
  {
    success: boolean;
    campusIdentified: boolean;
    emailDomain?: string;
    email: string;
    signupToken: string;
  },
  unknown,
  { token: string }
> =>
  useMutation(({ token }: { token: string }) =>
    globalState.client.verifySignupCredentialsToken(token),
  );

export const useVerifyRecoveryEmail = (): UseMutationResult<
  { success: boolean },
  unknown,
  {
    email: string;
    token: string;
  }
> =>
  useMutation(({ email, token }: { email: string; token: string }) =>
    globalState.client.verifyRecoveryEmail(email, token),
  );

export const useVerifyRecoveryEmailToken = (): UseMutationResult<
  {
    encryptedData: Uint8Array;
    legacySetup: boolean;
  },
  unknown,
  { token: string }
> =>
  useMutation(({ token }: { token: string }) =>
    globalState.client.verifyRecoveryEmailToken(token),
  );

export const useVerifyRecoveryEmailUpdate = (): UseMutationResult<
  { success: boolean },
  unknown,
  {
    email: string;
    phone: string;
    legacySetup?: boolean;
  }
> =>
  useMutation(
    ({
      email,
      phone,
      legacySetup,
    }: {
      email: string;
      phone: string;
      legacySetup?: boolean;
    }) =>
      globalState.client.verifyRecoveryEmailChange(email, phone, legacySetup),
  );

export const useSetUpAccountRecovery = (): UseMutationResult<
  { success: boolean },
  unknown,
  {
    consentsToFeedbackEmails: boolean;
    email: string;
    phoneNumber: string;
    securityQuestions: string[];
    answers: string[];
  }
> =>
  useMutation(
    ({
      consentsToFeedbackEmails,
      email,
      phoneNumber,
      securityQuestions,
      answers,
    }: {
      consentsToFeedbackEmails: boolean;
      email: string;
      phoneNumber: string;
      securityQuestions: string[];
      answers: string[];
    }) =>
      globalState.client.setUpAccountRecovery(
        consentsToFeedbackEmails,
        email,
        phoneNumber,
        securityQuestions,
        answers,
      ),
  );

export const useRequestAccountRecovery = (): UseMutationResult<
  { success: boolean },
  unknown,
  {
    emailAddress: string;
    phoneNumber: string;
  }
> =>
  useMutation(
    ({
      emailAddress,
      phoneNumber,
    }: {
      emailAddress: string;
      phoneNumber: string;
    }) =>
      globalState.client.submitAccountRecoveryRequest(
        emailAddress,
        phoneNumber,
      ),
  );

export const useValidateAnswers = (): UseMutationResult<
  boolean,
  unknown,
  {
    answer0: string;
    answer1: string;
    answer2: string;
    token: string;
  }
> =>
  useMutation(
    ({
      answer0,
      answer1,
      answer2,
      token,
    }: {
      answer0: string;
      answer1: string;
      answer2: string;
      token: string;
    }) =>
      globalState.client.validateSecurityQuestionAnswers(
        [answer0, answer1, answer2],
        token,
      ),
  );
